import React, { useEffect, useState } from 'react';
import { MailIcon, LockIcon, ArrowRightIcon, CheckCircleIcon } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useStory } from '../components/StoryContext';
type LoginResponse = {
  access_token?: string;
  error?: string;
};
export const ParentLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    setIsLoggedIn
  } = useStory();
  const searchParams = new URLSearchParams(location.search);
  const bookId = searchParams.get('book_id');
  const page = searchParams.get('page');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  // Store the previous location for redirect after login
  const [prevPath, setPrevPath] = useState<string>('/');
  // Capture the previous path when component mounts
  useEffect(() => {
    const referrer = document.referrer;
    if (referrer && referrer.includes(window.location.origin)) {
      // Extract just the path from the full referrer URL
      const url = new URL(referrer);
      const path = url.pathname + url.search;
      // Don't redirect back to login or create-account pages
      if (!path.includes('/login') && !path.includes('/create-account')) {
        setPrevPath(path);
      }
    } else {
      // Check if there's a state with a 'from' property (passed by React Router)
      const locationState = location.state as {
        from?: string;
      };
      if (locationState?.from && !locationState.from.includes('/login') && !locationState.from.includes('/create-account')) {
        setPrevPath(locationState.from);
      }
    }
  }, [location]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch('https://storybunny.messagesimproved.com/api/storybunny/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          password,
          book_id: bookId // Include book_id in the login request
        })
      });
      const data: LoginResponse = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Login failed. Please try again.');
      }
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('user_email', email.toLowerCase());
        setIsLoggedIn(true); // Set login state to true
        setShowSuccess(true);
        setTimeout(() => {
          if (bookId) {
            // Include page parameter if it exists
            const pageParam = page ? `&page=${page}` : '';
            navigate(`/story?book_id=${bookId}${pageParam}`);
          } else {
            // Redirect to previous path instead of garden
            navigate(prevPath);
          }
        }, 3000);
      } else {
        throw new Error('No access token received');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="min-h-screen bg-[#FFFBF6] flex flex-col" data-id="element-1445">
      {/* Content */}
      <div className="flex-1 flex items-center justify-center p-4" data-id="element-1446">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-id="element-1447">
          <div className="text-center mb-8" data-id="element-1448">
            <h2 className="text-3xl font-bold text-[#6B4B3E] mb-4" data-id="element-1449">
              Log in to Story Bunny
            </h2>
            <p className="text-[#6B4B3E]/75" data-id="element-1450">
              Continue your magical story adventure
            </p>
          </div>
          {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm" data-id="element-1451">
              {error}
            </div>}
          {showSuccess && <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm flex items-center" data-id="element-1452">
              <CheckCircleIcon size={20} className="mr-2" data-id="element-1453" />
              Login successful!{' '}
              {bookId ? 'Redirecting back to your story...' : 'Redirecting you back...'}
            </div>}
          <form onSubmit={handleSubmit} className="space-y-6" data-id="element-1454">
            <div data-id="element-1455">
              <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1456">
                Email
                <span className="text-red-500" data-id="element-1457">*</span>
              </label>
              <div className="relative" data-id="element-1458">
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} required disabled={isLoading || showSuccess} className="w-full pl-10 pr-4 py-2 border border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088] focus:ring-2 focus:ring-[#E5C1A9]/50 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="you@example.com" data-id="element-1459" />
                <MailIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#D4A088]" data-id="element-1460" />
              </div>
            </div>
            <div data-id="element-1461">
              <label className="block text-[#6B4B3E] font-medium mb-2" data-id="element-1462">
                Password
                <span className="text-red-500" data-id="element-1463">*</span>
              </label>
              <div className="relative" data-id="element-1464">
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} required disabled={isLoading || showSuccess} className="w-full pl-10 pr-4 py-2 border border-[#E5C1A9] rounded-full focus:outline-none focus:border-[#D4A088] focus:ring-2 focus:ring-[#E5C1A9]/50 disabled:opacity-50 disabled:cursor-not-allowed" placeholder="••••••••" data-id="element-1465" />
                <LockIcon size={20} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#D4A088]" data-id="element-1466" />
              </div>
            </div>
            <button type="submit" disabled={isLoading || showSuccess} className="w-full bg-[#D4A088] text-white py-3 rounded-full hover:bg-[#C28B73] transition-colors flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-1467">
              <span className="text-lg" data-id="element-1468">
                {isLoading ? 'Logging in...' : 'Log In'}
              </span>
              <ArrowRightIcon size={20} className="ml-2" data-id="element-1469" />
            </button>
          </form>
          <p className="text-center mt-6 text-[#6B4B3E]/60" data-id="element-1470">
            New to Story Bunny?{' '}
            <Link to={`/create-account${location.search}`} className="text-[#D4A088] hover:underline font-medium" state={{
            from: prevPath
          }} data-id="element-1471">
              Create an account
            </Link>
          </p>
        </div>
      </div>
      {/* Decorative Elements */}
      <div className="fixed bottom-0 left-0 w-24 h-24 p-4" data-id="element-1472">
        <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/s651TuhqMamgDGTkg6MHYM/ChatGPT_Image_May_17%2C_2025_at_10_47_42_AM.png" alt="Decorative bunny with megaphone" className="w-full h-full object-contain transform -rotate-12" data-id="element-1473" />
      </div>
      <div className="fixed bottom-0 right-0 w-24 h-24 p-4" data-id="element-1474">
        <Link to="/faq" data-id="element-1475">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/u4x2jgZDZtZbxX5UPi1T17/ChatGPT_Image_May_17%2C_2025_at_10_41_15_AM.png" alt="Decorative bunny with question mark" className="w-full h-full object-contain transform rotate-12 hover:scale-110 transition-transform cursor-pointer" data-id="element-1476" />
        </Link>
      </div>
    </div>;
};